import { t as tr } from "../i18n/ui";
import { type Point, roundedPath, seededRandom, shiftPoints } from "./math";

const W = 400;
const H = 320;
const STROKE =
  'stroke="#293133" stroke-width="1.2" fill="none" vector-effect="non-scaling-stroke" stroke-linecap="round"';

const pipe = (points: Point[], twin = true, opacity = 0.9): string => {
  let s = `<path d="${roundedPath(points, 10)}" ${STROKE} opacity="${opacity}"/>`;
  if (twin)
    s += `<path d="${roundedPath(shiftPoints(points, -8, 8), 10)}" ${STROKE} opacity="${opacity * 0.38}"/>`;
  return s;
};
const box = (x: number, y: number, w: number, h: number, opacity = 0.9, fill = "none"): string =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" ${STROKE.replace('fill="none"', `fill="${fill}"`)} opacity="${opacity}"/>`;
const svg = (body: string, vb = `0 0 ${W} ${H}`): string =>
  `<svg viewBox="${vb}" preserveAspectRatio="xMidYMid meet" aria-hidden="true">${body}</svg>`;

const VISUALS: Record<string, () => string> = {
  order: () =>
    svg(
      Array.from({ length: 12 }, (_, i) =>
        box(110 + (i % 4) * 46, 92 + Math.floor(i / 4) * 46, 34, 34, i < 9 ? 0.9 : 0.4),
      ).join("") +
        pipe([
          [-10, 240],
          [70, 240],
          [70, 160],
          [104, 160],
        ]) +
        pipe([
          [296, 110],
          [340, 110],
          [340, 40],
          [410, 40],
        ]),
    ),
  line: () => {
    let s = pipe([
      [-10, 160],
      [410, 160],
    ]);
    for (let i = 0; i < 6; i++)
      s += box(40 + i * 60, 140, 30, 40, 0.9, i === 3 ? "#FFFFFF" : "#84C3BE");
    return svg(
      s +
        pipe(
          [
            [205, 140],
            [205, 70],
            [300, 70],
            [300, 30],
          ],
          false,
          0.6,
        ),
    );
  },
  install: () => {
    let s = box(120, 40, 160, 240, 0.85);
    for (let r = 0; r < 5; r++)
      for (let c = 0; c < 3; c++)
        s += box(
          136 + c * 46,
          56 + r * 42,
          30,
          28,
          r === 2 && c === 1 ? 1 : 0.35,
          r === 2 && c === 1 ? "#FFFFFF" : "none",
        );
    return svg(
      s +
        pipe([
          [-10, 290],
          [200, 290],
          [200, 282],
        ]) +
        pipe(
          [
            [280, 140],
            [330, 140],
            [330, 210],
            [410, 210],
          ],
          false,
          0.6,
        ),
    );
  },
  monitor: () => {
    const v = [40, 44, 39, 46, 42, 48, 45, 47, 44, 49];
    const bars = v
      .map(
        (h, i) =>
          `<rect x="${92 + i * 22}" y="${220 - h * 2.4}" width="12" height="${h * 2.4}" rx="6" fill="#293133" opacity="${i === 9 ? 1 : 0.35}"/>`,
      )
      .join("");
    return svg(
      bars +
        pipe([
          [-10, 236],
          [410, 236],
        ]) +
        `<circle cx="306" cy="80" r="5" fill="#FFFFFF"/>`,
    );
  },
};

export function initVisuals(): void {
  document.querySelectorAll<HTMLElement>("[data-visual]").forEach((el) => {
    const draw = VISUALS[el.dataset.visual ?? ""];
    if (draw) el.innerHTML = draw();
  });
}

const ICON = {
  radio: {
    w: 16,
    h: 11,
    dot: "M7.1 4.6 L8.9 4.6 L8.9 6.4 L7.1 6.4 Z",
    inner:
      "M9.967 2.621 L11.227 5.5 L9.967 8.379 L11.433 9.021 L12.973 5.5 L11.433 1.979 Z M4.567 1.979 L3.027 5.5 L4.567 9.021 L6.033 8.379 L4.773 5.5 L6.033 2.621 Z",
    outer:
      "M11.661 1.205 L13.434 5.5 L11.661 9.795 L13.139 10.405 L15.166 5.5 L13.139 0.595 Z M2.861 0.595 L0.834 5.5 L2.861 10.405 L4.339 9.795 L2.566 5.5 L4.339 1.205 Z",
  },
  leak: {
    w: 14.4,
    h: 11.2,
    pipe: "M0 0 L1.85 0 L1.85 0.55 L12.55 0.55 L12.55 0 L14.4 0 L14.4 5.9 L12.55 5.9 L12.55 5.35 L1.85 5.35 L1.85 5.9 L0 5.9 Z M1.85 2.4 L12.55 2.4 L12.55 3.5 L1.85 3.5 Z",
    drops: "M4.8 7.9 L5.753 9.55 A1.1 1.1 0 1 1 3.847 9.55 Z M9.6 7.9 L10.553 9.55 A1.1 1.1 0 1 1 8.647 9.55 Z",
  },
  burst: {
    w: 14.4,
    h: 9.4,
    body: "M0 3.5 L1.85 3.5 L1.85 4.05 L4.8 4.05 L4.8 5.9 L1.85 5.9 L1.85 7 L12.55 7 L12.55 5.9 L9.6 5.9 L9.6 4.05 L12.55 4.05 L12.55 3.5 L14.4 3.5 L14.4 9.4 L12.55 9.4 L12.55 8.85 L1.85 8.85 L1.85 9.4 L0 9.4 Z M1.909 0.987 L3.091 0.064 L4.691 2.113 L3.509 3.036 Z M11.309 0.064 L12.491 0.987 L10.891 3.036 L9.709 2.113 Z M6.25 0.95 L8.15 0.95 L8.15 2.95 L6.25 2.95 Z",
  },
} as const;

const wave = (ms: number) => (ms < 0 ? 0 : ms < 220 ? ms / 220 : ms < 1000 ? 1 - (ms - 220) / 780 : 0);

export function initBuilding(canvas: HTMLCanvasElement, reduced: boolean): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const radio = { dot: new Path2D(ICON.radio.dot), inner: new Path2D(ICON.radio.inner), outer: new Path2D(ICON.radio.outer) };
  const leak = { pipe: new Path2D(ICON.leak.pipe), drops: new Path2D(ICON.leak.drops) };
  const burst = new Path2D(ICON.burst.body);
  const inIcon = (x: number, y: number, width: number, w: number, h: number, draw: () => void) => {
    const k = width / w;
    ctx.save();
    ctx.translate(x - (w * k) / 2, y - (h * k) / 2);
    ctx.scale(k, k);
    draw();
    ctx.restore();
  };
  let ink = "41,49,51";
  const readInk = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--surface-rgb");
    ink = v.replace(/\s/g, "") || "41,49,51";
  };
  readInk();
  window.addEventListener("otridon:theme", readInk);
  const rnd = seededRandom(3);
  const grid = 6;
  const last = new Float32Array(grid * grid).map(() => -rnd() * 9000);
  const root = canvas.closest(".sides__left");
  const beat = (p: number) => (p < 0.5 ? 1 : Math.max(0, 1 - (p - 0.5) / 0.18));
  const alarms = [
    { kind: "leak", index: 2 * grid + 4, rgb: "240,125,26", period: 1000, beat },
    { kind: "burst", index: 5 * grid + 1, rgb: "240,125,26", period: 1000, beat },
  ].map((a) => {
    const label = root?.querySelector<HTMLElement>(`[data-alarm="${a.kind}"]`) ?? null;
    return {
      ...a,
      label,
      blink: label?.querySelector<HTMLElement>("[data-alarm-blink]") ?? null,
      warp: root?.querySelector<SVGFEDisplacementMapElement>(`[data-dissolve="${a.kind}"]`) ?? null,
      box: { x: 0, y: 0, s: 0 },
      fading: 0,
    };
  });
  type Alarm = (typeof alarms)[number];
  let hover: Alarm | null = null;
  let w = 0;
  let h = 0;
  let visible = true;

  const size = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const unit = (cx: number, cy: number, b: number) => {
    const c = b * 0.3;
    const r = b * 0.12;
    const wall = Math.max(1, b * 0.045);
    const pts: [number, number][] = [
      [cx - b + c, cy - b],
      [cx + b - c, cy - b],
      [cx + b, cy - b + c],
      [cx + b, cy + b - c],
      [cx + b - c, cy + b],
      [cx - b + c, cy + b],
      [cx - b, cy + b - c],
      [cx - b, cy - b + c],
    ];
    const path = new Path2D();
    const first = pts[0] as [number, number];
    const end = pts[pts.length - 1] as [number, number];
    path.moveTo((first[0] + end[0]) / 2, (first[1] + end[1]) / 2);
    pts.forEach((p, k) => {
      const next = pts[(k + 1) % pts.length] as [number, number];
      path.arcTo(p[0], p[1], next[0], next[1], r);
    });
    path.closePath();
    const iw = b - wall;
    path.roundRect(cx - iw, cy - iw, 2 * iw, 2 * iw, iw * 0.4);
    const gap = Math.max(1.5, b * 0.12);
    path.rect(cx - b - 1, cy - gap / 2, wall + 1, gap);
    path.rect(cx + b - wall, cy - gap / 2, wall + 1, gap);
    return path;
  };

  const draw = (t: number) => {
    ctx.clearRect(0, 0, w, h);
    const side = Math.min(w, h) * 0.74;
    const bx = w * 0.12;
    const top = h * 0.12;
    const cell = side / grid;
    const ground = top + side + h * 0.05;
    ctx.lineWidth = 1;
    ctx.strokeStyle = `rgba(${ink},0.55)`;
    ctx.beginPath();
    ctx.moveTo(0, ground);
    ctx.lineTo(w, ground);
    ctx.stroke();
    ctx.strokeStyle = `rgba(${ink},0.2)`;
    ctx.beginPath();
    ctx.moveTo(0, ground + 8);
    ctx.lineTo(w, ground + 8);
    ctx.stroke();
    ctx.strokeStyle = `rgba(${ink},0.4)`;
    ctx.strokeRect(bx, top, side, side);
    ctx.strokeStyle = `rgba(${ink},0.1)`;
    for (let f = 1; f < grid; f++) {
      ctx.beginPath();
      ctx.moveTo(bx, top + f * cell);
      ctx.lineTo(bx + side, top + f * cell);
      ctx.stroke();
    }
    const b = cell * 0.3;
    const d = b * 0.42;
    for (let f = 0; f < grid; f++) {
      for (let c = 0; c < grid; c++) {
        const i = f * grid + c;
        const cx = bx + (c + 0.5) * cell;
        const cy = top + (grid - 1 - f) * cell + cell / 2;
        ctx.fillStyle = `rgba(${ink},0.7)`;
        ctx.fill(unit(cx, cy, b), "evenodd");
        const since = t - (last[i] ?? 0);
        const glow = Math.max(0, 1 - since / 1600);
        const alarm = alarms.find((a) => a.index === i);
        if (alarm) {
          const blink = reduced ? 1 : alarm.beat((t % alarm.period) / alarm.period);
          alarm.box = { x: cx, y: cy, s: b };
          ctx.save();
          ctx.shadowColor = `rgba(${alarm.rgb},${(0.9 * blink).toFixed(3)})`;
          ctx.shadowBlur = d * 3 * blink;
          ctx.fillStyle = `rgba(${alarm.rgb},${(0.2 + 0.8 * blink).toFixed(3)})`;
          if (alarm.kind === "leak") {
            const fall = reduced ? 0 : ((t % alarm.period) / alarm.period) * 0.9;
            inIcon(cx, cy, b * 1.05, ICON.leak.w, ICON.leak.h, () => {
              ctx.fill(leak.pipe, "evenodd");
              ctx.translate(0, fall);
              ctx.fill(leak.drops);
            });
          } else {
            inIcon(cx, cy, b * 1.05, ICON.burst.w, ICON.burst.h, () => ctx.fill(burst));
          }
          ctx.restore();
          if (alarm.label) alarm.label.style.top = `${cy}px`;
          if (alarm.blink) alarm.blink.style.opacity = (0.3 + 0.7 * blink).toFixed(3);
        } else {
          const parts: [Path2D, number, number][] = [
            [radio.dot, glow, 0.3],
            [radio.inner, wave(since - 120), 0.2],
            [radio.outer, wave(since - 340), 0.14],
          ];
          inIcon(cx, cy, b * 1.15, ICON.radio.w, ICON.radio.h, () => {
            for (const [path, lit, rest] of parts) {
              ctx.fillStyle =
                lit > 0.02 ? `rgba(255,255,255,${(0.3 + 0.7 * lit).toFixed(3)})` : `rgba(${ink},${rest})`;
              ctx.fill(path);
            }
          });
        }
        if (!reduced && since > 2600 + (i % 7) * 900 && rnd() < 0.02) last[i] = t;
      }
    }
    if (hover) {
      ctx.save();
      ctx.strokeStyle = `rgba(${hover.rgb},0.9)`;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(hover.box.x + hover.box.s + 4, hover.box.y);
      ctx.lineTo(w, hover.box.y);
      ctx.stroke();
      ctx.restore();
    }
    ctx.fillStyle = `rgba(${ink},0.9)`;
    ctx.font = `${Math.max(10, w * 0.016)}px "Fragment Mono", "Noto Kufi Arabic", monospace`;
    ctx.textAlign = "left";
    ctx.fillText(tr("MAIN METER · 3.48 M³/DAY"), bx, ground - 10);
  };

  const reset = (a: Alarm) => {
    const el = a.label;
    if (!el) return;
    el.style.filter = "";
    el.style.transform = "";
    el.style.letterSpacing = "";
    el.style.transition = "";
    el.style.opacity = "";
    el.style.visibility = "";
    a.warp?.setAttribute("scale", "0");
  };
  const ripple = (el: HTMLElement, fx: number) => {
    const r = el.getBoundingClientRect();
    el.dispatchEvent(
      new CustomEvent("water:ring", {
        bubbles: true,
        detail: {
          x: r.left + r.width * fx,
          y: r.top + r.height / 2,
          strength: -0.05,
          radius: 0.04,
        },
      }),
    );
  };
  const appear = (a: Alarm) => {
    cancelAnimationFrame(a.fading);
    reset(a);
    a.label?.classList.add("is-on");
  };
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const dissolve = (a: Alarm) => {
    const { label, warp } = a;
    if (!label) return;
    if (reduced || !warp) {
      label.classList.remove("is-on");
      return;
    }
    const start = performance.now();
    const length = 1700;
    const drops = [0.5, 0.2, 0.8];
    label.style.transition = "none";
    label.classList.remove("is-on");
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / length);
      const e = t * t * (3 - 2 * t);
      warp.setAttribute("scale", (90 * e).toFixed(1));
      const blur = `blur(${(6 * e).toFixed(2)}px)`;
      label.style.filter = coarse ? blur : `url(#dissolve-${a.kind}) ${blur}`;
      label.style.opacity = Math.max(0, 1 - Math.pow(t, 1.4)).toFixed(3);
      label.style.transform = `translateY(calc(-50% + ${(14 * e).toFixed(1)}px)) scale(${(1 + 0.06 * e).toFixed(3)})`;
      label.style.letterSpacing = `${(0.08 + 0.22 * e).toFixed(3)}em`;
      while (drops.length && t >= (3 - drops.length) * 0.25) ripple(label, drops.shift() ?? 0.5);
      if (t < 1) {
        a.fading = requestAnimationFrame(step);
        return;
      }
      reset(a);
      label.style.transition = "none";
      label.style.opacity = "0";
      label.style.visibility = "hidden";
    };
    cancelAnimationFrame(a.fading);
    a.fading = requestAnimationFrame(step);
  };

  const setHover = (next: Alarm | null) => {
    if (next === hover) return;
    if (hover) dissolve(hover);
    hover = next;
    canvas.style.cursor = next ? "pointer" : "";
    if (next) appear(next);
    if (reduced) draw(0);
  };
  const near = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    return (
      alarms.find((a) => {
        const reach = a.box.s * 1.2;
        return Math.abs(x - a.box.x) < reach && Math.abs(y - a.box.y) < reach;
      }) ?? null
    );
  };
  canvas.addEventListener("pointermove", (e) => {
    if (e.pointerType === "mouse") setHover(near(e));
  });
  canvas.addEventListener("pointerleave", (e) => {
    if (e.pointerType === "mouse") setHover(null);
  });
  let tapTimer = 0;
  canvas.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse") return;
    window.clearTimeout(tapTimer);
    const alarm = near(e);
    setHover(alarm);
    if (alarm) tapTimer = window.setTimeout(() => setHover(null), 3000);
  });

  size();
  new ResizeObserver(() => {
    size();
    draw(performance.now());
  }).observe(canvas);
  if (reduced) {
    draw(0);
    return;
  }
  let running = false;
  const loop = (t: number) => {
    if (!visible) {
      running = false;
      return;
    }
    draw(t);
    requestAnimationFrame(loop);
  };
  new IntersectionObserver(([e]) => {
    visible = Boolean(e?.isIntersecting);
    if (visible && !running) {
      running = true;
      requestAnimationFrame(loop);
    }
  }).observe(canvas);
}
